import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CircleDollarSign, X } from "lucide-react";
import { api, brl } from "../lib/api";
import { useAuth } from "./AuthContext";
import { playPaymentSound, primeNotificationAudio } from "../lib/paymentSound";

const NotifCtx = createContext(null);
const POLL_MS = 4000;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function detectDevice() {
  const ua = navigator.userAgent || "";
  const device = /iPhone|iPad|iPod/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : "Desktop";
  const browser = /CriOS|Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox"
    : /Safari/.test(ua) ? "Safari" : /Edg/.test(ua) ? "Edge" : "Outro";
  return { device, browser };
}

function timeLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export function relativeLabel(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "Agora mesmo";
    if (diff < 3600) return `${Math.floor(diff / 60)} min atras`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h atras`;
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch { return ""; }
}

function PaymentToast({ n, prefs, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 60, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 26 }}
      className="w-[340px] max-w-[88vw] overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-[0_18px_44px_rgba(16,120,80,0.28)]"
      data-testid="payment-toast"
    >
      <div className="flex items-stretch">
        <div className="w-1.5 shrink-0 bg-gradient-to-b from-emerald-400 to-emerald-600" />
        <div className="flex flex-1 items-start gap-3 p-4">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: [0.5, 1.25, 1] }}
            transition={{ duration: 0.5, times: [0, 0.6, 1] }}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-emerald-500/12 text-emerald-600"
          >
            <CircleDollarSign size={26} strokeWidth={2.2} />
          </motion.div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[13px] font-semibold text-emerald-700">Pagamento Recebido</div>
              <button onClick={onClose} className="text-[#9A8A98] hover:text-brand-dark" data-testid="payment-toast-close"><X size={15} /></button>
            </div>
            {prefs.show_client && (
              <div className="mt-1 truncate text-[15px] font-semibold text-brand-dark" data-testid="payment-toast-client">{n.client_name}</div>
            )}
            {n.reseller_name && n.reseller_name !== "-" && (
              <div className="text-[11px] text-[#6B5A69]">Revendedora: <span className="font-medium">{n.reseller_name}</span></div>
            )}
            {prefs.show_value && (
              <div className="mt-1 font-display text-2xl font-bold tabular text-emerald-600" data-testid="payment-toast-value">{brl(n.amount)}</div>
            )}
            <div className="mt-0.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">
              <span>{n.method === "pix" ? "PIX Mercado Pago" : n.method}</span>
              <span>·</span>
              <span>{timeLabel(n.paid_at || n.created_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [prefs, setPrefs] = useState({
    push_enabled: true, sound_enabled: true, toast_enabled: true, realtime_enabled: true,
    vibration_enabled: true, show_value: true, show_client: true, daily_summary: true,
    notif_payments: true, notif_charges: true, notif_clients: true, notif_promos: true,
    notif_system: true, sound: "pix_elegante", sound_options: [], is_admin: false,
  });
  const [pushState, setPushState] = useState({
    supported: typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window,
    permission: (typeof window !== "undefined" && "Notification" in window) ? Notification.permission : "unsupported",
    subscribed: false,
  });

  const cursorRef = useRef(null);
  const prefsRef = useRef(prefs);
  const queueRef = useRef([]);
  const drainingRef = useRef(false);
  prefsRef.current = prefs;

  const active = !!user && (user.role === "admin" || user.role === "reseller");

  const present = useCallback((n) => {
    const p = prefsRef.current;
    // Som
    if (p.sound_enabled) { try { playPaymentSound(p.sound); } catch { /* ignore */ } }
    // Vibracao (PWA/mobile)
    if (p.vibration_enabled && typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(120); } catch { /* ignore */ }
    }
    // Toast premium
    if (p.toast_enabled) {
      toast.custom((t) => <PaymentToast n={n} prefs={p} onClose={() => toast.dismiss(t)} />, { duration: 5000 });
    }
    // Notificacao do navegador (desktop)
    if (p.realtime_enabled && typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      try {
        const parts = [];
        if (p.show_client) parts.push(n.client_name);
        if (p.show_value) parts.push(brl(n.amount));
        new Notification("Pagamento recebido", { body: parts.join(" · "), tag: n.id });
      } catch { /* ignore */ }
    }
  }, []);

  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    while (queueRef.current.length) {
      const n = queueRef.current.shift();
      present(n);
      await new Promise((r) => setTimeout(r, 300)); // fila: 300ms entre pagamentos
    }
    drainingRef.current = false;
  }, [present]);

  // Carga inicial (sem tocar som) + preferencias
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: pf } = await api.get("/notifications/preferences");
        if (!cancelled && pf) setPrefs((prev) => ({ ...prev, ...pf }));
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/notifications", { params: { limit: 30 } });
        if (cancelled) return;
        setNotifications(data || []);
        cursorRef.current = (data && data[0]?.created_at) || new Date().toISOString();
        const { data: uc } = await api.get("/notifications/unread-count");
        if (!cancelled) setUnread(uc?.count || 0);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [active]);

  // Polling curto p/ novidades
  useEffect(() => {
    if (!active) return;
    const poll = async () => {
      if (!cursorRef.current) return;
      try {
        const { data } = await api.get("/notifications", { params: { since: cursorRef.current, limit: 20 } });
        if (!data || !data.length) return;
        const asc = [...data].reverse(); // mais antigo -> mais novo
        cursorRef.current = data[0].created_at; // data[0] e o mais novo
        setNotifications((prev) => [...asc.reverse(), ...prev].slice(0, 100));
        setUnread((u) => u + asc.length);
        queueRef.current.push(...asc);
        drain();
      } catch { /* ignore */ }
    };
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [active, drain]);

  // Pede permissao de notificacao no primeiro gesto + prime audio
  useEffect(() => {
    if (!active) return;
    const onGesture = () => {
      primeNotificationAudio();
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
        try {
          Notification.requestPermission().then((perm) => {
            setPushState((p) => ({ ...p, permission: perm }));
          });
        } catch { /* ignore */ }
      }
      window.removeEventListener("pointerdown", onGesture);
    };
    window.addEventListener("pointerdown", onGesture);
    return () => window.removeEventListener("pointerdown", onGesture);
  }, [active]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-read", { all: true });
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* ignore */ }
  }, []);

  const savePrefs = useCallback(async (patch) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
    try {
      const { data } = await api.put("/notifications/preferences", patch);
      if (data) setPrefs((prev) => ({ ...prev, ...data }));
    } catch { /* ignore */ }
  }, []);

  // Inscreve no Web Push (funciona no PWA/producao com Service Worker ativo)
  const subscribePush = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return false; // SW so e registrado em producao/PWA instalado
      const { data } = await api.get("/notifications/vapid-public-key");
      if (!data?.publicKey) return false;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      const meta = detectDevice();
      await api.post("/notifications/subscribe", { subscription: sub.toJSON(), ...meta });
      setPushState((p) => ({ ...p, subscribed: true, permission: Notification.permission }));
      return true;
    } catch { return false; }
  }, []);

  // Solicita permissao + inscreve (acionado por botao ou primeiro gesto)
  const enablePush = useCallback(async () => {
    if (!("Notification" in window)) return false;
    let perm = Notification.permission;
    if (perm === "default") { try { perm = await Notification.requestPermission(); } catch { /* ignore */ } }
    setPushState((p) => ({ ...p, permission: perm }));
    if (perm !== "granted") return false;
    await savePrefs({ push_enabled: true });
    return subscribePush();
  }, [savePrefs, subscribePush]);

  // Auto-inscreve quando ja ha permissao e o usuario quer push
  useEffect(() => {
    if (!active) return;
    if (prefs.push_enabled && pushState.permission === "granted") subscribePush();
  }, [active, prefs.push_enabled, pushState.permission, subscribePush]);

  return (
    <NotifCtx.Provider value={{ notifications, unread, prefs, pushState, markAllRead, savePrefs, enablePush, relativeLabel }}>
      {children}
    </NotifCtx.Provider>
  );
}

export const useNotifications = () => useContext(NotifCtx);
